import type { ChatState } from './reducer';
import type { ChatEffect, ChatEvent, ChatScript, Prompt } from './types';
import { chatReducer, clearEffects, initialChatState } from './reducer';

/**
 * Resuming a module by replaying it, not by snapshotting it.
 *
 * The reducer is pure and deterministic given a script, so a run is fully
 * described by the learner's inputs. Everything else — the thread, the cursor,
 * the counters, the score — is derived, and derived text is language-specific
 * where the decisions behind it are not. Persisting the inputs and replaying
 * them is therefore the only encoding that survives a language switch: the
 * German script replays the same answers into a German thread.
 *
 * The encoding is deliberately **positional**. A step's `id` is a Prismic slice
 * id, which is per document and so per language, and every option id is built
 * from it — storing either would make a saved run unreplayable the moment the
 * learner changed language. An option's position in its slice is the one thing
 * the translations agree on.
 */

/**
 * One learner input, in a form that outlives the document it was made against.
 * Pacing (`advance`) and the re-arm after wrong-answer feedback (`retry`) are
 * re-derived by the replay and never stored.
 */
export type PersistedEvent =
  /**
   * A quick reply, a quiz answer or one pick in a discovery, by the option's
   * position. A discovery's list shrinks as it is worked through, so the
   * position is the one in the list *as it stood* — which the replay rebuilds
   * exactly, because it re-derives every earlier pick first.
   */
  | { kind: 'option'; index: number }
  /** A checklist confirm, by the ticked options' positions. */
  | { kind: 'options'; indexes: Array<number> }
  /** Continue in the video player. */
  | { kind: 'gate' }
  /** A free-text submission. Its own words, so they replay verbatim. */
  | { kind: 'text'; value: string }
  /**
   * An activity card marking itself. `receipt` is the echo the card wrote, so
   * a run resumed in another language keeps the wording it was answered in.
   * `mistake` is positional like every other index here, and absent from
   * older runs.
   */
  | { kind: 'activity'; correct: boolean; receipt: string; mistake?: number }
  /** Giving up on an activity. */
  | { kind: 'reveal' };

/**
 * The persistable form of an event the learner just dispatched, or `null` when
 * the event is not a learner input (or does not fit the armed prompt).
 *
 * `prompt` must be the prompt as it stood *before* the dispatch.
 */
export function persistedEventFor(prompt: Prompt | null, event: ChatEvent): PersistedEvent | null {
  if (event.type === 'reveal') {
    return { kind: 'reveal' };
  }

  if (event.type !== 'answer' || prompt === null) {
    return null;
  }

  const options = 'options' in prompt ? prompt.options : [];
  const positionOf = (id: string) => options.findIndex((option) => option.id === id);
  const { payload } = event;

  switch (payload.kind) {
    case 'quick':
    case 'single':
    case 'discover': {
      const index = positionOf(payload.optionId);

      return index < 0 ? null : { kind: 'option', index };
    }

    case 'multi': {
      const indexes = payload.optionIds.map(positionOf);

      return indexes.some((index) => index < 0) ? null : { kind: 'options', indexes };
    }

    case 'gate':
      return { kind: 'gate' };

    case 'text':
      return { kind: 'text', value: payload.value };

    case 'activity': {
      const { correct, receipt, mistake } = payload;

      return mistake === undefined
        ? { kind: 'activity', correct, receipt }
        : { kind: 'activity', correct, receipt, mistake };
    }
  }
}

/**
 * The event a saved input becomes against the prompt now armed, or `null` when
 * the script no longer fits it — a slice reordered or removed, an option gone.
 * That `null` is what makes a stale run discardable rather than a crash.
 */
function chatEventFor(prompt: Prompt | null, event: PersistedEvent): ChatEvent | null {
  if (event.kind === 'reveal') {
    return { type: 'reveal' };
  }

  if (prompt === null) {
    return null;
  }

  switch (event.kind) {
    case 'option': {
      if (prompt.kind !== 'quick' && prompt.kind !== 'single' && prompt.kind !== 'discover') {
        return null;
      }

      const option = prompt.options[event.index];

      return option ? { type: 'answer', payload: { kind: prompt.kind, optionId: option.id } } : null;
    }

    case 'options': {
      if (prompt.kind !== 'multi') {
        return null;
      }

      const optionIds: Array<string> = [];

      for (const index of event.indexes) {
        const option = prompt.options[index];

        if (!option) {
          return null;
        }

        optionIds.push(option.id);
      }

      return { type: 'answer', payload: { kind: 'multi', optionIds } };
    }

    case 'gate':
      return prompt.kind === 'gate' ? { type: 'answer', payload: { kind: 'gate' } } : null;

    case 'text':
      return prompt.kind === 'text' ? { type: 'answer', payload: { kind: 'text', value: event.value } } : null;

    case 'activity':
      return prompt.kind === 'activity'
        ? {
            type: 'answer',
            payload: { kind: 'activity', correct: event.correct, receipt: event.receipt, mistake: event.mistake },
          }
        : null;
  }
}

/** Enough ticks for any authored module; a script that needs more is a bug. */
const MAX_TICKS = 1000;

/**
 * Pacing with the timers taken out: ticks `advance` until the chat wants an
 * answer or finishes, collecting the effects the store would have performed.
 */
function settle(state: ChatState, effects: Array<ChatEffect>): ChatState | null {
  let current = state;

  for (let tick = 0; tick < MAX_TICKS; tick += 1) {
    if (current.status === 'awaiting' || current.status === 'complete') {
      return current;
    }

    const next = chatReducer(current, { type: 'advance' });

    effects.push(...next.effects);
    current = clearEffects(next);
  }

  return null;
}

/** A run rebuilt as far as its inputs go. `effects` are the ones the live run already performed, in order. */
type Replayed = { state: ChatState; effects: Array<ChatEffect> };

export type ReplayResult =
  | ({ ok: true } & Replayed)
  /**
   * An input did not fit. `fitted` counts the inputs before it that did, and
   * `partial` is the run rebuilt from exactly those — settled at the prompt the
   * misfit was meant for — or `null` when not even the script's opening settled.
   */
  | { ok: false; reason: string; fitted: number; partial: Replayed | null };

/**
 * Rebuilds a run from its saved inputs, instantly and with no pacing.
 *
 * The caller gets the reducer state the live run would have been in and the
 * effects it emitted on the way — which it should apply to the counters but
 * never re-report to analytics, because they were reported the first time.
 */
export function replayRun(script: ChatScript, events: ReadonlyArray<PersistedEvent>): ReplayResult {
  const effects: Array<ChatEffect> = [];
  let state = settle(chatReducer(initialChatState, { type: 'start', script }), effects);

  if (state === null) {
    return { ok: false, reason: 'the script never settled', fitted: 0, partial: null };
  }

  for (const [position, event] of events.entries()) {
    const chatEvent = chatEventFor(state.pending ?? state.staged, event);
    const answered = chatEvent === null ? null : chatReducer(state, chatEvent);

    if (answered === null || answered === state) {
      const why = answered === null ? 'does not fit the script' : 'was refused';

      return {
        ok: false,
        reason: `input ${position + 1} (${event.kind}) ${why}`,
        fitted: position,
        partial: { state, effects },
      };
    }

    // Effects are collected on a copy, so a settle that never finishes leaves
    // `partial` holding only the effects of the inputs that fitted.
    const stepEffects = [...answered.effects];
    const next = settle(clearEffects(answered), stepEffects);

    if (next === null) {
      return { ok: false, reason: 'the script never settled', fitted: position, partial: { state, effects } };
    }

    effects.push(...stepEffects);
    state = next;
  }

  return { ok: true, state, effects };
}

export type ResumeDecision =
  /**
   * Install this state and drain these effects: the learner keeps their place.
   *
   * `kept` is how many saved inputs the state was rebuilt from. It is short of
   * the saved count only when the script now loaded stopped fitting part-way —
   * typically a translation authored with a slice more or fewer than the
   * language the run was started in — and the caller should then trim the
   * saved run to match, with `dropped` saying why.
   */
  | { action: 'resume'; state: ChatState; effects: Array<ChatEffect>; kept: number; dropped: string | null }
  /**
   * Play the script from the top, and forget whatever was saved.
   *
   * `discarded` carries a reason only when a run was thrown away unexpectedly —
   * a script that does not fit even its first input. Having nothing saved, or
   * having a finished run, is the ordinary case and reports `null`.
   */
  | { action: 'start'; discarded: string | null };

/**
 * Whether a saved run should be resumed, given the script now loaded.
 *
 * The whole resume policy, in one pure place:
 *
 * - a run that replays in full resumes where it was left — in whatever
 *   language the script is now, which is what makes a mid-module switch keep
 *   the learner's place;
 * - a run that stops fitting part-way resumes at the last input that did fit,
 *   so a script that differs between languages costs the learner the steps
 *   after the difference, not the whole module;
 * - a run that fits nothing starts over, and so does one that replays all the
 *   way to `complete` — finishing a module is what clears its run, so one that
 *   survived completion is a module the learner may start again from the top.
 */
export function resumableRun(
  script: ChatScript,
  run: { events: ReadonlyArray<PersistedEvent> } | undefined,
): ResumeDecision {
  if (!run || run.events.length === 0) {
    return { action: 'start', discarded: null };
  }

  const replayed = replayRun(script, run.events);

  if (!replayed.ok) {
    return replayed.fitted > 0 && replayed.partial !== null
      ? { action: 'resume', ...replayed.partial, kept: replayed.fitted, dropped: replayed.reason }
      : { action: 'start', discarded: replayed.reason };
  }

  if (replayed.state.status === 'complete') {
    return { action: 'start', discarded: null };
  }

  return { action: 'resume', state: replayed.state, effects: replayed.effects, kept: run.events.length, dropped: null };
}
